/**
 * Ledger — Phase 17 double-entry cashbook.
 *
 * URL params:
 *   ?party_type=traveller|vendor&party_id=<UCC|vendorId> — drill-down view
 *   &from=YYYY-MM-DD&to=YYYY-MM-DD — date range
 *   &trip_id=TRP_… — narrow to one trip
 *
 * Without `party_id`, shows the full unfiltered ledger (newest first).
 * Summary cards above the table read from /api/ledger/summary so they
 * always reflect the true balance, not just the filtered slice.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Plus, Download, Search, BookOpen, Wallet, ArrowDownCircle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const fmtINR = (n) => `₹${Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
const fmtDate = (s) => {
  if (!s) return '—';
  try { return new Date(s).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: '2-digit' }); }
  catch { return '—'; }
};
const initials = (s) => (s || '').trim().split(/\s+/).slice(0, 2).map((p) => p[0]).join('').toUpperCase() || '?';

const ENTRY_TYPE_LABEL = {
  invoice_issued:    'Invoice',
  payment_received:  'Payment',
  receipt_recorded:  'Receipt',
  vendor_expense:    'Expense',
  vendor_payment:    'Vendor Paid',
  vendor_advance:    'Vendor Advance',
  refund:            'Refund',
  vendor_credit:     'Vendor Credit',
  manual:            'Manual',
};

const todayISO = () => new Date().toISOString().slice(0, 10);
const firstOfMonthISO = () => {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10);
};

const Ledger = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [partyType, setPartyType] = useState(searchParams.get('party_type') || 'all');
  const [partyId, setPartyId] = useState(searchParams.get('party_id') || '');
  const [tripId, setTripId] = useState(searchParams.get('trip_id') || '');
  const [fromDate, setFromDate] = useState(searchParams.get('from') || firstOfMonthISO());
  const [toDate, setToDate] = useState(searchParams.get('to') || todayISO());
  const [partySearch, setPartySearch] = useState('');

  const [loading, setLoading] = useState(true);
  const [ledger, setLedger] = useState({ entries: [], opening_balance: 0, closing_balance: 0, total_debits: 0, total_credits: 0, party: null });
  const [summary, setSummary] = useState({ parties: [], totals: { receivable: 0, payable: 0, net: 0 } });
  const [recon, setRecon] = useState({ travellers: { count: 0, items: [] }, vendors: { count: 0, items: [] } });
  const [showRecon, setShowRecon] = useState(false);
  const [showManual, setShowManual] = useState(false);

  // ---- fetch ledger entries ------------------------------------------------
  const fetchLedger = async () => {
    setLoading(true);
    try {
      const q = new URLSearchParams();
      if (partyType !== 'all') q.set('party_type', partyType);
      if (partyId)             q.set('party_id', partyId);
      if (tripId)              q.set('trip_id', tripId);
      if (fromDate)            q.set('from_date', fromDate);
      if (toDate)              q.set('to_date', toDate);
      const r = await axios.get(`${BACKEND_URL}/api/ledger?${q.toString()}`, { withCredentials: true });
      setLedger(r.data);
    } catch {
      toast.error('Failed to load ledger');
    } finally {
      setLoading(false);
    }
  };
  const fetchSummary = async () => {
    try {
      const r = await axios.get(`${BACKEND_URL}/api/ledger/summary`, { withCredentials: true });
      setSummary(r.data);
    } catch { /* non-fatal */ }
  };
  const fetchRecon = async () => {
    try {
      const r = await axios.get(`${BACKEND_URL}/api/ledger/reconciliation`, { withCredentials: true });
      setRecon(r.data);
    } catch { /* non-fatal */ }
  };
  useEffect(() => { fetchLedger(); fetchSummary(); fetchRecon(); /* eslint-disable-next-line */ }, []);

  const applyFilters = () => {
    const next = new URLSearchParams();
    if (partyType !== 'all') next.set('party_type', partyType);
    if (partyId)             next.set('party_id', partyId);
    if (tripId)              next.set('trip_id', tripId);
    if (fromDate)            next.set('from', fromDate);
    if (toDate)              next.set('to', toDate);
    setSearchParams(next, { replace: true });
    fetchLedger();
  };

  const clearParty = () => {
    setPartyId(''); setPartyType('all');
    const next = new URLSearchParams();
    if (fromDate) next.set('from', fromDate);
    if (toDate)   next.set('to', toDate);
    setSearchParams(next, { replace: true });
    setTimeout(fetchLedger, 0);
  };

  const onExport = () => {
    const q = new URLSearchParams();
    if (partyType !== 'all') q.set('party_type', partyType);
    if (partyId)             q.set('party_id', partyId);
    if (fromDate)            q.set('from_date', fromDate);
    if (toDate)              q.set('to_date', toDate);
    window.open(`${BACKEND_URL}/api/ledger/export?${q.toString()}`, '_blank');
  };

  // Party search dropdown options — derived from the summary list. Cheap
  // client-side filter so the agent can type to filter without hitting
  // the API on every keystroke.
  const partyOptions = useMemo(() => {
    if (!partySearch) return summary.parties.slice(0, 50);
    const q = partySearch.toLowerCase();
    return summary.parties.filter((p) =>
      (p.party_name || '').toLowerCase().includes(q) ||
      (p.party_id || '').toLowerCase().includes(q),
    ).slice(0, 50);
  }, [partySearch, summary.parties]);

  return (
    <div className="space-y-3" data-testid="ledger-page">
      {/* HEADER ============================================================ */}
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900 flex items-center gap-2">
            <BookOpen className="h-5 w-5 text-emerald-600" /> Ledger
          </h1>
          <p className="text-xs text-slate-500 mt-0.5">
            {ledger.party?.name
              ? <>Drill-down: <strong className="text-slate-700">{ledger.party.name}</strong> · <button onClick={clearParty} className="text-teal-700 hover:underline" data-testid="ledger-clear-party">clear</button></>
              : 'Double-entry cashbook · all parties · all activity'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" onClick={() => setShowManual(true)} data-testid="ledger-manual-btn">
            <Plus className="h-3.5 w-3.5 mr-1" /> Add manual entry
          </Button>
          <Button size="sm" variant="outline" onClick={onExport} data-testid="ledger-export-btn">
            <Download className="h-3.5 w-3.5 mr-1" /> Export CSV
          </Button>
        </div>
      </div>

      {/* SUMMARY CARDS ===================================================== */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2" data-testid="ledger-summary-cards">
        <SummaryCard
          testid="card-receivable"
          label="Total receivable"
          value={fmtINR(summary.totals.receivable)}
          hint="Travellers who owe you"
          tone="rose"
        />
        <SummaryCard
          testid="card-payable"
          label="Total payable"
          value={fmtINR(summary.totals.payable)}
          hint="Owed to vendors"
          tone="amber"
        />
        <SummaryCard
          testid="card-net"
          label="Net position"
          value={fmtINR(summary.totals.net)}
          hint={summary.totals.net >= 0 ? 'In your favour' : 'Outflow'}
          tone={summary.totals.net >= 0 ? 'emerald' : 'rose'}
        />
      </div>

      {/* RECONCILIATION CARD ================================================
          Phase 17.1 — flags every party whose ledger doesn't balance.
          Compact when ALL parties reconcile; expandable list when not. */}
      <ReconciliationCard
        recon={recon}
        expanded={showRecon}
        onToggle={() => setShowRecon((v) => !v)}
        onPick={(p) => {
          // Jump straight to the party drill-down so the operator can chase
          // the unreconciled balance immediately.
          setPartyType(p.party_type);
          setPartyId(p.party_id);
          const next = new URLSearchParams();
          next.set('party_type', p.party_type);
          next.set('party_id', p.party_id);
          setSearchParams(next, { replace: true });
          setShowRecon(false);
          fetchLedger();
        }}
      />

      {/* FILTER BAR ======================================================== */}
      <Card className="p-3 border border-slate-200 rounded-[10px]">
        <div className="grid grid-cols-2 md:grid-cols-6 gap-2 items-end">
          <div>
            <Label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Party type</Label>
            <Select value={partyType} onValueChange={setPartyType}>
              <SelectTrigger className="h-8 text-xs" data-testid="filter-party-type">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All parties</SelectItem>
                <SelectItem value="traveller">Travellers only</SelectItem>
                <SelectItem value="vendor">Vendors only</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Party</Label>
            <div className="relative">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3 w-3 text-slate-400" />
              <Input
                value={partySearch}
                onChange={(e) => setPartySearch(e.target.value)}
                placeholder="Search name or ID"
                data-testid="filter-party-search"
                className="h-8 text-xs pl-7"
              />
              {partySearch && (
                <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-slate-200 rounded-md shadow-lg z-50 max-h-60 overflow-auto" data-testid="party-results">
                  {partyOptions.length === 0 && (
                    <div className="px-2 py-1.5 text-xs text-slate-400">No matches</div>
                  )}
                  {partyOptions.map((p) => (
                    <button
                      type="button"
                      key={`${p.party_type}-${p.party_id}`}
                      onClick={() => {
                        setPartyType(p.party_type);
                        setPartyId(p.party_id);
                        setPartySearch('');
                      }}
                      className="block w-full text-left px-2 py-1.5 text-xs hover:bg-slate-50"
                      data-testid={`party-pick-${p.party_id}`}
                    >
                      <span className="font-medium text-slate-800">{p.party_name}</span>
                      <span className="text-slate-400 ml-2">({p.party_type})</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">From</Label>
            <Input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} data-testid="filter-from-date" className="h-8 text-xs" />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">To</Label>
            <Input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} data-testid="filter-to-date" className="h-8 text-xs" />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Trip ID</Label>
            <Input value={tripId} onChange={(e) => setTripId(e.target.value)} placeholder="TRP_…" data-testid="filter-trip-id" className="h-8 text-xs" />
          </div>
          <Button onClick={applyFilters} size="sm" className="bg-emerald-700 hover:bg-emerald-800 text-white h-8 text-xs" data-testid="filter-apply-btn">
            Apply filters
          </Button>
        </div>
      </Card>

      {/* TABLE ============================================================= */}
      <LedgerTable loading={loading} ledger={ledger} />

      {/* MANUAL ENTRY MODAL ================================================ */}
      <ManualEntryDialog
        open={showManual}
        onOpenChange={setShowManual}
        partyOptions={summary.parties}
        onCreated={() => { fetchLedger(); fetchSummary(); }}
      />
    </div>
  );
};

/**
 * Reconciliation card — Phase 17.1.
 *
 * Compact 1-line summary when there's nothing to chase ("All balanced ✓"),
 * expandable to a per-party list when there are unreconciled rows.
 * Clicking a row jumps straight to the party drill-down so the operator
 * can resolve the mismatch right away.
 */
const ReconciliationCard = ({ recon, expanded, onToggle, onPick }) => {
  const tCount = recon.travellers?.count || 0;
  const vCount = recon.vendors?.count || 0;
  const total = tCount + vCount;
  if (total === 0) {
    return (
      <Card className="p-2.5 border border-emerald-200 bg-emerald-50/60 rounded-[10px]" data-testid="recon-card">
        <div className="flex items-center gap-2 text-xs">
          <span className="inline-flex items-center justify-center h-5 w-5 rounded-full bg-emerald-100 text-emerald-700">✓</span>
          <span className="font-semibold text-emerald-800">All balanced</span>
          <span className="text-slate-500">— every party's invoices equal their receipts, and every vendor's expenses equal their payments.</span>
        </div>
      </Card>
    );
  }
  return (
    <Card className="border border-amber-200 bg-amber-50/40 rounded-[10px]" data-testid="recon-card">
      <button
        type="button"
        onClick={onToggle}
        data-testid="recon-toggle"
        className="w-full text-left px-3 py-2 flex items-center gap-3 hover:bg-amber-50/80 transition-colors"
      >
        <span className="inline-flex items-center justify-center h-5 w-5 rounded-full bg-amber-100 text-amber-700 font-bold text-[11px]">!</span>
        <div className="flex-1 min-w-0">
          <div className="text-xs font-semibold text-slate-800">
            {total} parties unreconciled
            <span className="text-slate-500 font-normal ml-1.5">
              ({tCount} traveller{tCount !== 1 ? 's' : ''} · {vCount} vendor{vCount !== 1 ? 's' : ''})
            </span>
          </div>
          <div className="text-[10px] text-slate-500 mt-0.5">
            Receivable not yet collected: {fmtINR(recon.travellers?.total_outstanding || 0)} · Vendor payments due: {fmtINR(recon.vendors?.total_outstanding || 0)}
          </div>
        </div>
        <span className="text-[11px] font-semibold text-amber-700">{expanded ? 'Hide ▴' : 'View unreconciled →'}</span>
      </button>
      {expanded && (
        <div className="px-3 pb-3 pt-1 grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="recon-expanded">
          <ReconList label="Travellers (invoices ≠ receipts)" items={recon.travellers?.items || []} onPick={onPick} testidBase="recon-traveller" />
          <ReconList label="Vendors (expenses ≠ payments)"   items={recon.vendors?.items || []}    onPick={onPick} testidBase="recon-vendor" />
        </div>
      )}
    </Card>
  );
};

const ReconList = ({ label, items, onPick, testidBase }) => (
  <div data-testid={`${testidBase}-list`}>
    <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1.5">{label}</div>
    {items.length === 0 ? (
      <div className="text-xs text-slate-400 italic">All balanced ✓</div>
    ) : (
      <ul className="space-y-1">
        {items.map((p) => (
          <li key={`${p.party_type}-${p.party_id}`}>
            <button
              type="button"
              onClick={() => onPick(p)}
              data-testid={`${testidBase}-${p.party_id}`}
              className="w-full text-left flex items-center gap-2 px-2 py-1 rounded hover:bg-white border border-transparent hover:border-slate-200 transition-colors"
            >
              <span className="shrink-0 h-5 w-5 rounded-full bg-slate-100 text-slate-600 text-[9px] font-bold inline-flex items-center justify-center">
                {initials(p.party_name)}
              </span>
              <span className="text-xs font-medium text-slate-800 truncate flex-1">{p.party_name || '—'}</span>
              <span className={`text-xs font-mono font-bold ${p.balance > 0 ? 'text-rose-600' : 'text-emerald-700'}`}>
                {p.balance > 0 ? '+' : ''}{fmtINR(p.balance)}
              </span>
            </button>
          </li>
        ))}
      </ul>
    )}
  </div>
);

const SummaryCard = ({ label, value, hint, tone, testid }) => {
  const tones = {
    rose:    { bg: 'bg-rose-50',    border: 'border-rose-200',    text: 'text-rose-700',    icon: ArrowDownCircle },
    amber:   { bg: 'bg-amber-50',   border: 'border-amber-200',   text: 'text-amber-700',   icon: Wallet },
    emerald: { bg: 'bg-emerald-50', border: 'border-emerald-200', text: 'text-emerald-700', icon: Wallet },
  };
  const t = tones[tone] || tones.emerald;
  const Icon = t.icon;
  return (
    <Card data-testid={testid} className={`px-3 py-2.5 border ${t.border} ${t.bg} rounded-[10px] flex items-center gap-3`}>
      <div className={`shrink-0 w-9 h-9 rounded-md bg-white/80 flex items-center justify-center ${t.text}`}>
        <Icon className="h-4 w-4" />
      </div>
      <div className="min-w-0">
        <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">{label}</div>
        <div className={`text-[20px] font-bold ${t.text} leading-none mt-1 truncate`}>{value}</div>
        <div className="text-[10px] text-slate-500 mt-0.5">{hint}</div>
      </div>
    </Card>
  );
};

const LedgerTable = ({ loading, ledger }) => {
  const entries = ledger.entries || [];
  const total = ledger.closing_balance;
  // Phase 17.x — relabel the amount columns so each row reads naturally.
  // The stored debit/credit VALUES are unchanged (accounting-correct party
  // ledger); only the headers + a legend clarify the direction, fixing the
  // misleading "Debit (OUT)/Credit (IN)" cash-flow framing.
  const pType = ledger.party && ledger.party.type;
  const labels = pType === 'traveller'
    ? { debit: 'Received', credit: 'Invoiced', balance: 'Balance due',
        hint: 'Invoiced = billed to the customer · Received = paid by them · Balance due = still owed to you' }
    : pType === 'vendor'
    ? { debit: 'Paid', credit: 'Billed', balance: 'Balance owed',
        hint: 'Billed = raised by the vendor · Paid = paid to them · Balance owed = you still owe' }
    : { debit: 'Debit', credit: 'Credit', balance: 'Balance',
        hint: 'Credit = amount billed (invoice issued / expense raised) · Debit = amount settled (payment received / vendor paid) · Balance = outstanding' };
  return (
    <Card className="border border-slate-200 rounded-[10px] overflow-hidden" data-testid="ledger-table-card">
      <div className="px-3 py-1.5 bg-slate-50/70 border-b border-slate-100 text-[10px] text-slate-500" data-testid="ledger-legend">
        {labels.hint}
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-xs" data-testid="ledger-table">
          <thead className="bg-slate-50 sticky top-0 z-10">
            <tr>
              <Th>Date</Th>
              <Th>Party</Th>
              <Th>Description</Th>
              <Th>Trip</Th>
              <Th className="text-right">{labels.credit}</Th>
              <Th className="text-right">{labels.debit}</Th>
              <Th className="text-right">{labels.balance}</Th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {loading ? (
              <tr><td colSpan={7} className="text-center py-8 text-slate-400">Loading…</td></tr>
            ) : entries.length === 0 ? (
              <tr><td colSpan={7} className="text-center py-8 text-slate-400">No entries match the current filters.</td></tr>
            ) : (
              <>
                {ledger.opening_balance !== 0 && (
                  <tr data-testid="ledger-opening-row" className="bg-slate-50/60">
                    <td className="px-3 py-2 text-slate-500">Opening</td>
                    <td colSpan={3} className="px-3 py-2 text-slate-500 italic">Brought forward</td>
                    <td className="px-3 py-2 text-right text-slate-400">—</td>
                    <td className="px-3 py-2 text-right text-slate-400">—</td>
                    <td className={`px-3 py-2 text-right font-mono font-semibold ${ledger.opening_balance >= 0 ? 'text-emerald-700' : 'text-rose-700'}`}>
                      {fmtINR(ledger.opening_balance)}
                    </td>
                  </tr>
                )}
                {entries.map((e) => <Row key={e._id} e={e} />)}
              </>
            )}
          </tbody>
          {entries.length > 0 && (
            <tfoot className="bg-slate-50 sticky bottom-0">
              <tr data-testid="ledger-totals-row">
                <td colSpan={4} className="px-3 py-2 text-[11px] uppercase tracking-wider text-slate-600 font-semibold">Total</td>
                <td className="px-3 py-2 text-right font-mono font-bold text-emerald-700">{fmtINR(ledger.total_credits)}</td>
                <td className="px-3 py-2 text-right font-mono font-bold text-rose-600">{fmtINR(ledger.total_debits)}</td>
                <td className={`px-3 py-2 text-right font-mono font-bold ${total > 0 ? 'text-emerald-700' : total < 0 ? 'text-rose-600' : 'text-slate-400'}`}>
                  {fmtINR(total)}
                </td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </Card>
  );
};

const Th = ({ children, className = '' }) => (
  <th className={`px-3 py-2 text-[10px] uppercase tracking-wider text-slate-500 font-semibold text-left ${className}`}>{children}</th>
);

const Row = ({ e }) => {
  const isManual = e.entry_type === 'manual';
  const debit = Number(e.debit || 0);
  const credit = Number(e.credit || 0);
  const balance = Number(e.balance || 0);
  const balColor = balance > 0 ? 'text-emerald-700' : balance < 0 ? 'text-rose-600' : 'text-slate-400';
  return (
    <tr className="hover:bg-slate-50" data-testid={`ledger-row-${e._id}`} style={{ height: 40 }}>
      <td className="px-3 py-1.5 text-slate-600 font-mono whitespace-nowrap">{fmtDate(e.date)}</td>
      <td className="px-3 py-1.5">
        <div className="flex items-center gap-2 min-w-0">
          <span className="shrink-0 h-6 w-6 rounded-full bg-slate-100 text-slate-600 text-[10px] font-bold inline-flex items-center justify-center">
            {initials(e.party_name)}
          </span>
          <div className="min-w-0">
            <div className="text-[12px] font-semibold text-slate-800 truncate">{e.party_name || '—'}</div>
            <div className="text-[10px] text-slate-400 font-mono truncate">{e.party_id} · {e.party_type}</div>
          </div>
        </div>
      </td>
      <td className="px-3 py-1.5">
        <div className="flex items-center gap-1.5">
          <span className="text-[12px] text-slate-700 truncate max-w-[280px]" title={e.description}>{e.description}</span>
          {isManual && (
            <span className="text-[9px] font-semibold px-1.5 py-0.5 rounded-full bg-violet-50 text-violet-700 border border-violet-200">Manual</span>
          )}
        </div>
      </td>
      <td className="px-3 py-1.5">
        {(e.trip_display_id || e.trip_id) ? (
          <span className="text-[10px] font-mono text-slate-500 bg-slate-100 px-1.5 py-0.5 rounded">{e.trip_display_id || e.trip_id}</span>
        ) : <span className="text-slate-300">—</span>}
      </td>
      <td className="px-3 py-1.5 text-right font-mono font-bold text-emerald-700">{credit > 0 ? fmtINR(credit) : '—'}</td>
      <td className="px-3 py-1.5 text-right font-mono font-bold text-rose-600">{debit > 0 ? fmtINR(debit) : '—'}</td>
      <td className={`px-3 py-1.5 text-right font-mono font-bold ${balColor}`}>{fmtINR(balance)}</td>
    </tr>
  );
};

const ManualEntryDialog = ({ open, onOpenChange, partyOptions = [], onCreated }) => {
  const [form, setForm] = useState({
    party_type: 'traveller',
    party_id: '',
    party_name: '',
    date: todayISO(),
    description: '',
    amount: '',
    direction: 'debit',
    trip_id: '',
    note: '',
  });
  const [saving, setSaving] = useState(false);
  const [partyQ, setPartyQ] = useState('');

  const filtered = useMemo(() => {
    const ps = partyOptions.filter((p) => p.party_type === form.party_type);
    if (!partyQ) return ps.slice(0, 30);
    const q = partyQ.toLowerCase();
    return ps.filter((p) =>
      (p.party_name || '').toLowerCase().includes(q) ||
      (p.party_id || '').toLowerCase().includes(q),
    ).slice(0, 30);
  }, [partyQ, partyOptions, form.party_type]);

  const submit = async () => {
    const amount = Number(form.amount || 0);
    if (!form.party_id) { toast.error('Pick a party'); return; }
    if (!form.description.trim()) { toast.error('Description required'); return; }
    if (amount <= 0) { toast.error('Amount must be > 0'); return; }
    setSaving(true);
    try {
      await axios.post(`${BACKEND_URL}/api/ledger/manual`, {
        party_type: form.party_type,
        party_id: form.party_id,
        party_name: form.party_name,
        date: form.date,
        description: form.description,
        debit: form.direction === 'debit' ? amount : 0,
        credit: form.direction === 'credit' ? amount : 0,
        trip_id: form.trip_id || null,
        note: form.note || '',
      }, { withCredentials: true });
      toast.success('Manual entry added');
      onCreated?.();
      onOpenChange(false);
      setForm({ party_type: 'traveller', party_id: '', party_name: '', date: todayISO(), description: '', amount: '', direction: 'debit', trip_id: '', note: '' });
      setPartyQ('');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to add entry');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="ledger-manual-dialog">
        <DialogHeader>
          <DialogTitle>Add manual entry</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 mt-2">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Party type</Label>
              <Select value={form.party_type} onValueChange={(v) => setForm({ ...form, party_type: v, party_id: '', party_name: '' })}>
                <SelectTrigger className="mt-1" data-testid="manual-party-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="traveller">Traveller</SelectItem>
                  <SelectItem value="vendor">Vendor</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Date</Label>
              <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="manual-date" className="mt-1" />
            </div>
          </div>
          <div>
            <Label className="text-xs">Party *</Label>
            {form.party_id ? (
              <div className="mt-1 flex items-center gap-2 p-2 border border-slate-200 rounded-md bg-slate-50">
                <span className="text-sm font-medium text-slate-800">{form.party_name}</span>
                <span className="text-[10px] font-mono text-slate-400">{form.party_id}</span>
                <button onClick={() => setForm({ ...form, party_id: '', party_name: '' })} className="ml-auto text-[10px] text-rose-600 hover:underline" data-testid="manual-clear-party">change</button>
              </div>
            ) : (
              <>
                <Input
                  value={partyQ}
                  onChange={(e) => setPartyQ(e.target.value)}
                  placeholder="Type to search a party name or UCC / vendor ID…"
                  data-testid="manual-party-search"
                  className="mt-1"
                />
                {filtered.length > 0 && partyQ && (
                  <div className="mt-1 border border-slate-200 rounded-md max-h-32 overflow-auto" data-testid="manual-party-results">
                    {filtered.map((p) => (
                      <button
                        key={`${p.party_type}-${p.party_id}`}
                        type="button"
                        onClick={() => { setForm({ ...form, party_id: p.party_id, party_name: p.party_name }); setPartyQ(''); }}
                        className="block w-full text-left px-2 py-1.5 text-xs hover:bg-slate-50"
                        data-testid={`manual-pick-${p.party_id}`}
                      >
                        <span className="font-medium">{p.party_name}</span>
                        <span className="text-slate-400 ml-2">({p.party_id})</span>
                      </button>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
          <div>
            <Label className="text-xs">Description *</Label>
            <Input
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              placeholder="e.g. Opening balance adjustment, write-off…"
              data-testid="manual-description"
              className="mt-1"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Direction</Label>
              <Select value={form.direction} onValueChange={(v) => setForm({ ...form, direction: v })}>
                <SelectTrigger className="mt-1" data-testid="manual-direction">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="debit">Debit (money out)</SelectItem>
                  <SelectItem value="credit">Credit (money in)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Amount ₹ *</Label>
              <Input
                type="number"
                min="0"
                value={form.amount}
                onChange={(e) => setForm({ ...form, amount: e.target.value })}
                placeholder="0"
                data-testid="manual-amount"
                className="mt-1"
              />
            </div>
          </div>
          <div>
            <Label className="text-xs">Trip ID (optional)</Label>
            <Input value={form.trip_id} onChange={(e) => setForm({ ...form, trip_id: e.target.value })} placeholder="TRP_…" data-testid="manual-trip-id" className="mt-1" />
          </div>
          <div>
            <Label className="text-xs">Internal note</Label>
            <Textarea rows={2} value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} data-testid="manual-note" className="mt-1" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="manual-cancel-btn">Cancel</Button>
          <Button onClick={submit} disabled={saving} className="bg-emerald-700 hover:bg-emerald-800 text-white" data-testid="manual-save-btn">
            {saving ? 'Adding…' : 'Add entry'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default Ledger;
